import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  Injector,
  computed,
  effect,
  inject,
  input,
  linkedSignal,
  output,
  signal
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { catchError, map, of, take } from 'rxjs';

import { UserEntityRegistrations } from '@core/models/entities/implementations/user-entity-registrations';
import { UserRouteV1Model } from '@core/models/entities/implementations/user-route-v1';
import { PlanFilterOperator } from '@core/models/entity/entity-selector-request.model';
import { KNOWN_FEATURE_FLAGS } from '@core/models/feature-flags/known-feature-flags';
import {
  PlanEditGesture,
  RegistrationResource
} from '@core/models/plan-edit-gesture.model';
import { FeatureFlagsFacade } from '@core/services/feature-flags/feature-flags.facade';
import { ICONS } from '@core/services/icons/icons.service';
import { SelectorsService } from '@core/services/selectors/selectors.service';
import { selectTimelineVisualization } from '@core/store/data-visualization/data-visualization.selectors';
import { selectDataspaceState } from '@core/store/dataspace/dataspace.selectors';
import { PlanEditSessionActions } from '@core/store/plan-edit/plan-edit-session.actions';
import { selectPlanWatchForView } from '@core/store/plan-edit/plan-edit-session.selectors';
import { selectPlan } from '@core/store/plan/plan.selectors';
import { allocateRouteRef, moveIntoRoute } from '@core/utils/plan-edit/targets';

import { SelectItem } from '@shared/components/select/select-item.model';
import { SelectComponent } from '@shared/components/select/select.component';

import { EntityAutoCompleteService } from '@features/components/entities/entity-auto-complete-dialog/entity-auto-complete.service';

import { knownRouteResources } from '../known-route-resources';
import { PlanEditFormsService } from '../plan-edit-forms.service';
import { RegistrationNameKind, ResourceKind, injectResourceNames } from '../resource-names';

interface ResourceField {
  kind: ResourceKind;
  icon: string;
  value: string | null;
  required: boolean;
}

type RegistrationKind = 'vehicle' | 'trailer' | 'driver';

const REGISTRATION_KINDS: {
  kind: RegistrationKind;
  resource: RegistrationResource;
  nameKind: RegistrationNameKind;
  entityType: string;
  versionName: string;
}[] = [
  {
    kind: 'vehicle',
    resource: 'VEHICLE',
    nameKind: 'vehicle_registration',
    entityType: 'VEHICLE',
    versionName: 'UserVehicle'
  },
  {
    kind: 'trailer',
    resource: 'TRAILER',
    nameKind: 'trailer_registration',
    entityType: 'TRAILER',
    versionName: 'UserTrailer'
  },
  {
    kind: 'driver',
    resource: 'DRIVER',
    nameKind: 'driver_registration',
    entityType: 'DRIVER',
    versionName: 'UserDriver'
  }
];

/** The select's "no named registration" entry; a registration code never takes this value. */
const RECOMPUTE_CHOICE = '__recompute__';

interface RegistrationField {
  kind: RegistrationKind;
  items: SelectItem[];
  value: string;
}

/**
 * Changes a route's vehicle, driver, trailer and company in one gesture, so a vehicle swap the
 * current company would refuse can clear or change the company at the same time. Without a route it
 * opens a new one with the chosen resources, then moves `then`'s item to its end. A resource with
 * named registrations also offers a Registration select, sent after the resources in the same gesture.
 */
@Component({
  selector: 'app-route-resources-form',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslatePipe, SelectComponent],
  templateUrl: './route-resources-form.component.html',
  styles: ``
})
export class RouteResourcesFormComponent {
  readonly routeUid = input.required<string | null>();
  readonly then = input<PlanEditGesture | null>(null);
  /** What `then` moves into the new route, as the form names it. */
  readonly carriedName = input<string | null>(null);
  readonly done = output<void>();

  private readonly store = inject(Store);
  private readonly picker = inject(EntityAutoCompleteService);
  private readonly forms = inject(PlanEditFormsService);
  private readonly injector = inject(Injector);
  private readonly timeline = this.store.selectSignal(selectTimelineVisualization);
  private readonly watch = this.store.selectSignal(selectPlanWatchForView);
  private readonly selectors = inject(SelectorsService);
  private readonly translate = inject(TranslateService);
  private readonly dataspaceState = this.store.selectSignal(selectDataspaceState);
  private readonly plan = this.store.selectSignal(selectPlan);
  private readonly currentLang = toSignal(
    this.translate.onLangChange.pipe(map((event) => event.lang)),
    { initialValue: this.translate.getCurrentLang() }
  );
  private readonly registrationsEnabled = inject(FeatureFlagsFacade).isActive(
    KNOWN_FEATURE_FLAGS.ENABLE_LIVE_PLAN_EDIT_REGISTRATIONS
  );

  protected readonly ICONS = ICONS;

  private readonly known = computed(() =>
    knownRouteResources(
      this.timeline().rows,
      (this.watch().overlay.upserted['UserRoute'] ?? {}) as unknown as Record<
        string,
        UserRouteV1Model
      >
    )
  );
  protected readonly isNew = computed(() => this.routeUid() === null);
  private readonly current = computed(() => {
    const routeUid = this.routeUid();
    return routeUid === null ? undefined : this.known().get(routeUid);
  });

  protected readonly vehicle = linkedSignal(() => this.current()?.vehicleUid ?? '');
  protected readonly driver = linkedSignal<string | null>(
    () => this.current()?.driverUid ?? null
  );
  protected readonly trailer = linkedSignal<string | null>(
    () => this.current()?.trailerUid ?? null
  );

  protected readonly company = linkedSignal<string | null>(
    () => this.current()?.companyUid ?? null
  );

  protected readonly fields = computed<ResourceField[]>(() => [
    { kind: 'vehicle', icon: ICONS.truck, value: this.vehicle() || null, required: true },
    { kind: 'driver', icon: ICONS.user, value: this.driver(), required: false },
    { kind: 'trailer', icon: ICONS.truckTrailer, value: this.trailer(), required: false },
    { kind: 'company', icon: ICONS.factory, value: this.company(), required: false }
  ]);

  /** The named registrations each picked resource offers, by `kind:uid`. */
  private readonly namedRegistrations = signal<ReadonlyMap<string, readonly string[]>>(
    new Map()
  );
  /** Keys already loading or loaded, so a change elsewhere never refetches a pending one. */
  private readonly requestedRegistrations = new Set<string>();
  private readonly destroyRef = inject(DestroyRef);
  /** What the planner chose per resource, valid only while that resource stays picked. */
  private readonly registrationPicks = signal<
    Partial<Record<RegistrationKind, { uid: string; code: string | null }>>
  >({});

  protected readonly resourceName = injectResourceNames(
    computed(() => [
      ...this.fields().map((field) => ({ kind: field.kind, uid: field.value })),
      ...REGISTRATION_KINDS.flatMap(({ kind, nameKind }) =>
        (this.namedRegistrations().get(this.registrationKey(kind)) ?? []).map((code) => ({
          kind: nameKind,
          uid: code
        }))
      )
    ])
  );

  protected readonly registrationFields = computed<RegistrationField[]>(() => {
    if (this.isNew() || !this.registrationsEnabled()) return [];
    this.currentLang();
    return REGISTRATION_KINDS.flatMap(({ kind, nameKind }) => {
      const codes = this.namedRegistrations().get(this.registrationKey(kind));
      if (!codes?.length) return [];
      return [
        {
          kind,
          items: [
            {
              id: RECOMPUTE_CHOICE,
              label: this.translate.instant('features.plan.edit.resources.recompute_choose')
            },
            ...codes.map((code) => ({ id: code, label: this.resourceName(nameKind, code) }))
          ],
          value: this.registrationValue(kind) ?? RECOMPUTE_CHOICE
        }
      ];
    });
  });

  /** The registrations whose choice differs from what the route holds, for the gesture. */
  private readonly changedRegistrations = computed(() => {
    const changed: Partial<Record<RegistrationResource, string | null>> = {};
    for (const { kind, resource } of REGISTRATION_KINDS) {
      if (!this.registrationFields().some((field) => field.kind === kind)) continue;
      const value = this.registrationValue(kind);
      if (value !== this.registrationBaseline(kind)) changed[resource] = value;
    }
    return changed;
  });

  /** Other routes that already run the picked vehicle. */
  protected readonly vehicleAlsoOn = computed(() =>
    [...this.known().values()].filter(
      (other) => other.routeUid !== this.routeUid() && other.vehicleUid === this.vehicle()
    )
  );

  private readonly companyChanged = computed(
    () => !!this.current() && this.current()!.companyUid !== this.company()
  );

  private readonly hasRegistrationChange = computed(
    () => Object.keys(this.changedRegistrations()).length > 0
  );

  protected readonly changed = computed(() => {
    const current = this.current();
    return (
      !!current &&
      (current.vehicleUid !== this.vehicle() ||
        current.driverUid !== this.driver() ||
        current.trailerUid !== this.trailer() ||
        this.companyChanged() ||
        this.hasRegistrationChange())
    );
  });

  protected readonly canApply = computed(
    () => !!this.vehicle() && (this.isNew() || this.changed())
  );

  constructor() {
    effect(() => {
      const dataSpaceId = this.dataspaceState()?.dataspace?.id;
      if (!this.registrationsEnabled() || dataSpaceId === undefined || this.isNew()) return;
      const planId = this.plan()?.id;
      const planFilter = planId
        ? { id: planId, operator: PlanFilterOperator.IN }
        : undefined;
      for (const { kind, entityType, versionName } of REGISTRATION_KINDS) {
        const uid = this.fieldUid(kind);
        const key = this.registrationKey(kind);
        if (!uid || this.requestedRegistrations.has(key)) continue;
        this.requestedRegistrations.add(key);
        this.selectors
          .selectEntityByUid$(entityType, versionName, uid, dataSpaceId, planFilter)
          .pipe(
            take(1),
            catchError(() => of(undefined)),
            takeUntilDestroyed(this.destroyRef)
          )
          .subscribe((entity) => {
            const registrations = (
              entity?.raw as { registrations?: UserEntityRegistrations } | undefined
            )?.registrations;
            this.namedRegistrations.update((known) =>
              new Map(known).set(
                key,
                registrations?.type === 'Nominal' ? registrations.registration_uids : []
              )
            );
          });
      }
    });
  }

  protected registrationField(kind: string): RegistrationField | undefined {
    return this.registrationFields().find((field) => field.kind === kind);
  }

  protected pickRegistration(kind: RegistrationKind, id: unknown): void {
    const uid = this.fieldUid(kind);
    if (!uid) return;
    const code = id === RECOMPUTE_CHOICE || typeof id !== 'string' ? null : id;
    this.registrationPicks.update((picks) => ({ ...picks, [kind]: { uid, code } }));
  }

  private fieldUid(kind: RegistrationKind): string | null {
    return (kind === 'vehicle' ? this.vehicle() : this[kind]()) || null;
  }

  private registrationKey(kind: RegistrationKind): string {
    return `${kind}:${this.fieldUid(kind)}`;
  }

  /** A replaced resource drops its registration, so only an unchanged one keeps the held code. */
  private registrationBaseline(kind: RegistrationKind): string | null {
    const current = this.current();
    const uid = this.fieldUid(kind);
    const held = current && current[`${kind}Uid`];
    return current && uid && held === uid ? current.registrationCodes[kind] : null;
  }

  private registrationValue(kind: RegistrationKind): string | null {
    const pick = this.registrationPicks()[kind];
    return pick && pick.uid === this.fieldUid(kind)
      ? pick.code
      : this.registrationBaseline(kind);
  }

  protected pick(kind: ResourceKind): void {
    const picked = signal<string | string[] | null>(null);
    const held = this.fields().find((field) => field.kind === kind)?.value;
    this.picker.show(kind, picked, held ? [held] : [], false);
    const watcher = effect(
      () => {
        const value = picked();
        if (typeof value === 'string') {
          this.set(kind, value);
          watcher.destroy();
        } else if (!this.picker.isVisible()) {
          watcher.destroy();
        }
      },
      { injector: this.injector }
    );
  }

  protected clear(kind: 'driver' | 'trailer' | 'company'): void {
    this.set(kind, null);
  }

  protected swapInstead(otherRouteUid: string): void {
    const anchorEl = this.forms.state()?.anchorEl;
    const routeUid = this.routeUid();
    if (!anchorEl || routeUid === null) return;
    this.forms.open(anchorEl, { kind: 'swap', routeUid, otherRouteUid });
  }

  protected apply(): void {
    if (!this.canApply()) return;
    const routeUid = this.routeUid();
    const routeRef = routeUid === null ? allocateRouteRef() : '';
    const then = this.then();
    const gesture: PlanEditGesture =
      routeUid === null
        ? {
            kind: 'open-route',
            routeRef,
            vehicleUid: this.vehicle(),
            driverUid: this.driver(),
            trailerUid: this.trailer(),
            companyUid: this.company(),
            then: then && moveIntoRoute(then, routeRef)
          }
        : {
            kind: 'set-resources',
            resources: {
              routeUid,
              vehicleUid: this.vehicle(),
              driverUid: this.driver(),
              trailerUid: this.trailer()
            },
            ...(this.companyChanged() ? { companyUid: this.company() } : {}),
            ...(this.hasRegistrationChange()
              ? { registrations: this.changedRegistrations() }
              : {})
          };
    this.store.dispatch(PlanEditSessionActions.gestureRequested({ gesture }));
    this.done.emit();
  }

  private set(kind: ResourceKind, value: string | null): void {
    if (kind === 'vehicle') this.vehicle.set(value ?? '');
    else if (kind === 'driver') this.driver.set(value);
    else if (kind === 'trailer') this.trailer.set(value);
    else this.company.set(value);
  }
}
